// apps/roleforge-app/src/lib/use-dialog-focus.ts
//
// Issue #1221 — the audit modals declared `aria-modal` but never took focus
// and never trapped it, so a keyboard or screen-reader user stayed parked on
// the page behind the dialog. This hook gives any `role="dialog"` container
// the three behaviours `aria-modal` promises:
//
//   1. focus moves into the dialog on open,
//   2. Tab / Shift+Tab cycle inside the dialog instead of escaping to the page,
//   3. Escape closes it and focus returns to whatever opened it.
//
// It is deliberately dependency-free so every dialog in the audit experience
// can adopt it without pulling in a modal library.

import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((el) => el.getAttribute('aria-hidden') !== 'true');
}

/**
 * Wire focus management for a modal dialog.
 *
 * Attach the returned ref to the element carrying `role="dialog"`.
 * `onClose` is called when the user presses Escape.
 */
export function useDialogFocus<T extends HTMLElement = HTMLDivElement>(
  onClose?: () => void,
) {
  const containerRef = useRef<T | null>(null);
  // Keep the latest onClose without re-running the effect (and therefore
  // without stealing focus again) on every parent render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const previouslyFocused =
      typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // 1. Move focus into the dialog. Prefer the first real control; fall back
    //    to the dialog itself so the screen reader still announces the title.
    const initial = focusableWithin(container)[0];
    if (initial) {
      initial.focus();
    } else {
      container.setAttribute('tabindex', '-1');
      container.focus();
    }

    // 2 + 3. Trap Tab and handle Escape.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = focusableWithin(container);
      if (focusables.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (active === first || active === container || !container.contains(active)) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (active === last || active === container || !container.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    // A single document-level listener: it also catches the case where focus
    // has somehow landed outside the dialog and pulls it back in.
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the control that opened the dialog.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, []);

  return containerRef;
}
